import {
  addDays,
  addWeeks,
  endOfMonth,
  endOfQuarter,
  endOfYear,
  isSameDay,
  startOfDay,
  isBefore,
  isAfter,
} from "date-fns";
import { useTranslation } from "@/packages/utils";
import { useDatePickerContext } from "./DatePickerContext";
import type { DatePickerPresetsProps, DatePreset } from "./types";

function useDefaultPresets(): DatePreset[] {
  const { t } = useTranslation(["common"]);
  const today = startOfDay(new Date());
  return [
    { label: t("datePicker.today"), date: today },
    { label: t("datePicker.tomorrow"), date: addDays(today, 1) },
    { label: t("datePicker.inOneWeek"), date: addWeeks(today, 1) },
    { label: t("datePicker.inTwoWeeks"), date: addWeeks(today, 2) },
    { label: t("datePicker.in30Days"), date: addDays(today, 30) },
    { label: t("datePicker.endOfMonth"), date: endOfMonth(today) },
    { label: t("datePicker.endOfQuarter"), date: endOfQuarter(today) },
    { label: t("datePicker.endOfYear"), date: endOfYear(today) },
  ];
}

export function DatePickerPresets({
  presets: customPresets,
  className,
}: DatePickerPresetsProps) {
  const { pendingDate, setPendingDate, minDate, maxDate } =
    useDatePickerContext();

  const defaultPresets = useDefaultPresets();
  const presets = customPresets ?? defaultPresets;

  const filteredPresets = presets.filter((preset) => {
    const day = startOfDay(preset.date);
    if (minDate && isBefore(day, startOfDay(minDate))) return false;
    if (maxDate && isAfter(day, startOfDay(maxDate))) return false;
    return true;
  });

  return (
    <div
      className={`flex flex-col gap-0.5 py-2 px-2 min-w-[140px] ${className ?? ""}`}
    >
      {filteredPresets.map((preset) => {
        const isActive = pendingDate
          ? isSameDay(startOfDay(preset.date), startOfDay(pendingDate))
          : false;

        return (
          <button
            key={preset.label}
            type="button"
            onClick={() => setPendingDate(startOfDay(preset.date))}
            className={`text-left text-xs px-2 py-1.5 rounded-md transition-colors cursor-pointer
              ${isActive ? "text-accent bg-accent/10" : "text-secondary hover:text-primary hover:bg-card-hover"}
            `}
          >
            {preset.label}
          </button>
        );
      })}
    </div>
  );
}
